import { useEffect, useMemo, useRef, useState } from "react";
import {
  Card,
  Input,
  Button,
  Typography,
  Space,
  Popover,
  Switch,
  Spin,
  Alert,
} from "antd";
import {
  Link2,
  Copy,
  Eye,
  Share2,
  Mail,
  Save,
} from "lucide-react";
import { WhatsAppOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import {
  getPublicMeetingLinks,
  createPublicMeetingLink,
  updatePublicMeetingLink,
} from "../../actionCreator/publicMeetingLinkActionCreator";
import { getMeetingTypes } from "../../actionCreator/meetingTypesActionCreator";
import { getAvailabilitySlots } from "../../actionCreator/availabilityActionCreator";
import EmailForm from "../../common/EmailForm";
import { getIntegrationSummary } from "../../actionCreator/calendarActionCreator";
import { useAlert } from "../../context/AlertProvider";

const originURL = window.location.origin + "/schedule-meeting";

const { Text } = Typography;

const BookingLinkPage = () => {
  const dispatch = useDispatch();
  const { showAlert } = useAlert();

  const [personalURL, setPersonalURL] = useState("");
  const [personalUrlError, setPersonalUrlError] = useState("");
  const [isActive, setIsActive] = useState(true);

  const { publicMeetingLinks, loading } = useSelector(
    (state) => state.publicMeetingLinkReducer,
  );
  const { meetingTypes } = useSelector(
    (state) => state.meetingTypeReducer,
  );
  const integrationSummary = useSelector(
    (state) => state.calendarReducer.integrationSummary,
  );
  const availabilitySlots = useSelector(
    (state) => state.availabilityReducer.availabilitySlots,
  );

  const isGoogleConnected =
    integrationSummary?.sources?.find((d) => d?.name === "Google Calendar")
      ?.status === "Connected";

  const hasAvailability = useMemo(() => {
    if (!Array.isArray(availabilitySlots) || availabilitySlots.length === 0) {
      return false;
    }
    return availabilitySlots.some(
      (day) =>
        (Array.isArray(day?.timeSlots) && day.timeSlots.length > 0) ||
        (day?.startTime && day?.endTime),
    );
  }, [availabilitySlots]);

  const activeLink =
    publicMeetingLinks && publicMeetingLinks.length > 0
      ? publicMeetingLinks[0]
      : null;

  useEffect(() => {
    dispatch(getPublicMeetingLinks());
    dispatch(getMeetingTypes());
    dispatch(getIntegrationSummary());
    dispatch(getAvailabilitySlots());
  }, [dispatch]);

  useEffect(() => {
    if (activeLink) {
      setPersonalURL(activeLink.customUrl || "");
      setIsActive(activeLink.isActive !== false);
      setPersonalUrlError("");
    }
  }, [activeLink]);

  const fullURL = personalURL ? `${originURL}/${personalURL}` : originURL;
  const popoverRef = useRef(null);
  const savedCustomUrl = activeLink?.customUrl || "";
  const savedIsActive = activeLink ? activeLink.isActive !== false : true;
  const savedFullURL = savedCustomUrl
    ? `${originURL}/${savedCustomUrl}`
    : originURL;
  const hasFormChanges = useMemo(() => {
    return (
      personalURL.trim() !== savedCustomUrl.trim() || isActive !== savedIsActive
    );
  }, [personalURL, savedCustomUrl, isActive, savedIsActive]);

  const handleCopy = () => {
    if (!savedCustomUrl) return showAlert("warning", "Please save your link first!");
    if (!hasAvailability) {
      return showAlert("warning", "Availability must be added to your account before activating and sharing your booking link.");
    }
    if (!savedIsActive) {
      return showAlert("warning", "Booking link is not active. Please activate to share with someone.");
    }
    navigator.clipboard.writeText(savedFullURL);
    showAlert("success", "Booking link copied!");
  };

  const handleWhatsAppShare = () => {
    if (!personalURL)
      return showAlert("warning", "Please save your Personal URL first!");
    const whatsappURL = `https://wa.me/?text=${encodeURIComponent(fullURL)}`;
    window.open(whatsappURL, "_blank");
  };

  const handleSave = async () => {
    if (!personalURL.trim()) {
      setPersonalUrlError("Personal URL cannot be empty!");
      return showAlert("error", "Personal URL cannot be empty!");
    }

    if (isActive && !hasAvailability) {
      return showAlert(
        "warning",
        "Availability must be added to your account before your booking link can be activated.",
      );
    }

    if (isActive && (!meetingTypes || meetingTypes.length === 0)) {
      return showAlert(
        "warning",
        "You must create at least one meeting type before your booking link can be activated.",
      );
    }

    if (isActive && !isGoogleConnected) {
      return showAlert(
        "warning",
        "Google Calendar integration is required before publishing your booking page.",
      );
    }

    setPersonalUrlError("");
    const payload = {
      customUrl: personalURL,
      isActive: isActive,
    };

    let res;
    if (activeLink && activeLink.id) {
      res = await dispatch(updatePublicMeetingLink(activeLink.id, payload));
    } else {
      res = await dispatch(createPublicMeetingLink(payload));
    }

    if (res?.status === 200 || res?.status === 201) {
      setPersonalUrlError("");
      showAlert("success", "Booking link settings saved!");
      dispatch(getPublicMeetingLinks());
    } else {
      const apiErrorTitle = res?.response?.data?.title;
      if (apiErrorTitle === "Custom URL must be unique.") {
        setPersonalUrlError(
          "This URL suffix is already in use. Please choose a different one.",
        );
      } else {
        showAlert("error", apiErrorTitle || "Failed to save link settings.");
      }
    }
  };

  const shareContent = (
    <div className="p-2">
      <div className="fw-semibold mb-2 text-[14px]">Share Link</div>

      <div className="d-flex flex-column gap-2">
        <EmailForm recipientEmail="">
          <div className="d-flex align-items-center gap-2 cursor-pointer p-2 rounded-md transition-all">
            <div className="p-2 rounded-full d-flex align-items-center justify-content-center">
              <Mail size={16} />
            </div>
            <span>Send via Email</span>
          </div>
        </EmailForm>

        <div
          onClick={handleWhatsAppShare}
          className="d-flex align-items-center gap-2 cursor-pointer p-2 rounded-md transition-all"
        >
          <div className="p-2 rounded-full d-flex align-items-center justify-content-center">
            <WhatsAppOutlined className="text-22c55e" />
          </div>
          <span>Share on WhatsApp</span>
        </div>
      </div>
    </div>
  );

  return (
    <Spin spinning={loading}>
      <div className="tw-py-6">
        <div className="tw-max-w-3xl tw-mx-auto">
          <div className="tw-mt-4 tw-mb-6">
            <h2 className="tw-text-lg tw-font-bold tw-text-slate-800 tw-mb-1">
              Personal Booking Link
            </h2>
            <p className="tw-text-sm tw-text-slate-500">
              Share your unique booking URL with clients and prospects.
            </p>
          </div>

          <Card
            className="!tw-rounded-2xl !tw-border-slate-100 tw-shadow-[0_10px_30px_rgba(0,0,0,0.02)]"
          >
            <div>
              <Space
                direction="vertical"
                className="tw-w-full"
                size="large"
              >
                {!hasAvailability ? (
                  <Alert
                    type="warning"
                    showIcon
                    className="tw-rounded-xl"
                    message={
                      <span className="tw-font-bold tw-text-sm">
                        Availability schedule is required to activate your booking page.
                      </span>
                    }
                    description={
                      <span className="tw-text-xs tw-mt-0.5 tw-block">
                        This booking page cannot be activated until availability is added to your account. Go to the{" "}
                        <a
                          onClick={() => {
                            const params = new URLSearchParams(window.location.search);
                            params.set("tab", "1");
                            window.location.search = params.toString();
                          }}
                          className="tw-text-theme tw-underline tw-font-semibold tw-cursor-pointer"
                        >
                          Availability
                        </a>{" "}
                        tab to set up your available schedule.
                      </span>
                    }
                  />
                ) : !meetingTypes || meetingTypes.length === 0 ? (
                  <Alert
                    type="warning"
                    showIcon
                    className="tw-rounded-xl"
                    message={
                      <span className="tw-font-bold tw-text-sm">
                        At least one meeting type is required to activate your booking page.
                      </span>
                    }
                    description={
                      <span className="tw-text-xs tw-mt-0.5 tw-block">
                        This booking page will not work until you create at least one meeting type. Go to the{" "}
                        <a
                          onClick={() => {
                            const params = new URLSearchParams(window.location.search);
                            params.set("tab", "2");
                            window.location.search = params.toString();
                          }}
                          className="tw-text-theme tw-underline tw-font-semibold tw-cursor-pointer"
                        >
                          Meetings
                        </a>{" "}
                        tab to set one up.
                      </span>
                    }
                  />
                ) : !isGoogleConnected ? (
                  <Alert
                    type="warning"
                    showIcon
                    className="tw-rounded-xl"
                    message={
                      <span className="tw-font-bold tw-text-sm">
                        Google Calendar integration is required before publishing.
                      </span>
                    }
                    description={
                      <span className="tw-text-xs tw-mt-0.5 tw-block">
                        This booking page will not work until Google Calendar is connected, so publishing is not allowed yet.
                      </span>
                    }
                  />
                ) : null}

                <div>
                  <Text className="tw-text-sm tw-font-bold tw-text-slate-700 tw-flex tw-items-center tw-gap-2">
                    <Link2 size={16} /> Your Booking URL
                  </Text>
                  <Text className="tw-text-xs tw-text-slate-400 tw-mt-1 tw-block">
                    Customize and share your personal booking link
                  </Text>
                </div>

                <div className="tw-flex tw-flex-col sm:tw-flex-row sm:tw-items-center sm:tw-justify-between tw-gap-4 tw-p-4 tw-rounded-2xl tw-bg-slate-50/50 tw-border tw-border-solid tw-border-slate-100/80">
                  <div>
                    <span className="tw-text-sm tw-font-bold tw-text-slate-700 tw-block">
                      Active Status
                    </span>
                    <span className="tw-text-xs tw-text-slate-400 tw-mt-1 tw-block">
                      Turn off to temporarily disable people from booking new meetings.
                    </span>
                  </div>
                  <Switch
                    checked={
                      isActive &&
                      hasAvailability &&
                      meetingTypes &&
                      meetingTypes.length > 0 &&
                      isGoogleConnected
                    }
                    onChange={(checked) => {
                      if (checked && !hasAvailability) {
                        showAlert(
                          "warning",
                          "Availability must be added to your account before your booking link can be activated.",
                        );
                        return;
                      }
                      if (checked && (!meetingTypes || meetingTypes.length === 0)) {
                        showAlert(
                          "warning",
                          "You must create at least one meeting type before your booking link can be activated.",
                        );
                        return;
                      }
                      if (checked && !isGoogleConnected) {
                        showAlert(
                          "warning",
                          "Connect Google Calendar before publishing your booking page.",
                        );
                        return;
                      }
                      setIsActive(checked);
                    }}
                  />
                </div>

                <div>
                  <label className="tw-text-sm tw-font-bold tw-text-slate-700 tw-block tw-mb-2">
                    Personal URL
                  </label>
                  <div
                    className={`tw-flex tw-items-stretch tw-w-full tw-rounded-xl tw-border tw-border-solid ${
                      personalUrlError
                        ? "tw-border-red-400 focus-within:tw-ring-red-200"
                        : "tw-border-slate-200 focus-within:tw-border-[#5D44FF] focus-within:tw-ring-[#5D44FF]/20"
                    } focus-within:tw-ring-2 tw-bg-white tw-overflow-hidden tw-transition-all`}
                  >
                    <span className="tw-flex tw-items-center tw-px-3.5 tw-bg-slate-50/80 tw-text-slate-500 tw-text-xs sm:tw-text-sm tw-font-medium tw-border-r tw-border-solid tw-border-slate-200 tw-whitespace-nowrap tw-select-none">
                      {originURL}/
                    </span>
                    <Input
                      value={personalURL}
                      variant="borderless"
                      onChange={(e) => {
                        setPersonalURL(
                          e.target.value.replace(/[^a-zA-Z0-9-]/g, ""),
                        );
                        setPersonalUrlError("");
                      }}
                      placeholder="e.g. john-doe"
                      className="!tw-h-[44px] !tw-px-3.5 !tw-text-slate-800 !tw-text-sm !tw-font-medium !tw-bg-transparent !tw-shadow-none"
                    />
                  </div>
                  {personalUrlError ? (
                    <Text type="danger" className="tw-mt-1.5 tw-text-xs tw-block">
                      {personalUrlError}
                    </Text>
                  ) : null}
                  <span className="tw-text-xs tw-text-slate-400 tw-mt-1.5 tw-block">
                    Choose a unique identifier for your booking page. Only
                    alphanumeric characters and hyphens allowed.
                  </span>
                </div>

                <div className="tw-bg-[#fbfbfe] tw-border tw-border-solid tw-border-slate-100 tw-p-5 tw-rounded-2xl">
                  <label className="tw-text-xs tw-font-bold tw-text-slate-400 tw-uppercase tw-tracking-wider tw-block tw-mb-2">
                    Live Preview Link
                  </label>
                  <div className="tw-flex tw-align-items-center tw-gap-3">
                    <Input value={fullURL} readOnly className="!tw-h-[42px] !tw-rounded-xl !tw-border-slate-200" />
                    <Button
                      icon={<Copy size={16} />}
                      onClick={handleCopy}
                      className="!tw-h-[42px] !tw-rounded-xl !tw-border-slate-200 !tw-bg-white hover:!tw-border-slate-400 hover:!tw-text-slate-800 tw-transition-all tw-duration-200 tw-px-5"
                    >
                      Copy
                    </Button>
                  </div>
                </div>

                <div className="tw-flex tw-justify-end tw-align-items-center tw-gap-3 tw-mt-6">
                  {savedCustomUrl ? (
                    <>
                      <Button
                        icon={<Eye size={16} />}
                        className="!tw-h-[42px] !tw-rounded-xl !tw-border-slate-200 !tw-bg-white !tw-text-slate-600 hover:!tw-border-slate-400 hover:!tw-text-slate-800 tw-transition-all tw-duration-200"
                        onClick={() => {
                          window.open(savedFullURL, "_blank");
                        }}
                      >
                        Open Live Page
                      </Button>
                      <Popover
                        content={shareContent}
                        trigger="click"
                        placement="bottomRight"
                        ref={popoverRef}
                      >
                        <Button
                          className="!tw-h-[42px] !tw-rounded-xl !tw-border-slate-200 !tw-bg-white !tw-text-slate-600 hover:!tw-border-slate-400 hover:!tw-text-slate-800 tw-transition-all tw-duration-200"
                          icon={<Share2 size={16} />}
                        >
                          Share Link
                        </Button>
                      </Popover>
                    </>
                  ) : null}
                  <Button
                    type="primary"
                    className="theme-btn !tw-h-[42px] !tw-rounded-xl tw-font-bold tw-text-sm tw-shadow-[0_8px_20px_rgba(93,68,255,0.25)] hover:tw-shadow-[0_12px_24px_rgba(93,68,255,0.35)] hover:tw-scale-[1.01] tw-transition-all tw-duration-300"
                    icon={<Save size={16} />}
                    onClick={handleSave}
                    disabled={
                      !hasFormChanges ||
                      (isActive &&
                        (!hasAvailability ||
                          !isGoogleConnected ||
                          !meetingTypes ||
                          meetingTypes.length === 0))
                    }
                  >
                    Save Settings
                  </Button>
                </div>
              </Space>
            </div>
          </Card>
        </div>
      </div>
    </Spin>
  );
};

export default BookingLinkPage;
